import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Bot,
  Copy,
  Loader2,
  Pause,
  Pencil,
  Play,
  Sparkles,
  Trash2,
} from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Modal } from "../../../../components/ui/Modal";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { getAiAgentStats, isEndpointMissing } from "./aiAgentsApi";
import {
  AGENT_STATUS_LABELS,
  AGENT_STATUS_TONES,
  agentDisabledScopeLabels,
  audienceLabel,
  detectScopeConflicts,
  providerSummary,
} from "./agentsListHelpers";
import { normalizeCaller } from "./aiErrorMessage";

// Lista de agentes IA del courier. Presentación pura: quién carga, guarda o
// borra es la página (AiSettings), que recarga la lista después de cada acción.
// Acá solo viven dos estados locales: qué tarjeta está mostrando sus métricas y
// qué agente espera confirmación de borrado (el Modal de confirmación).

// Botón de acción de tarjeta: mismo alto táctil que el resto de la vista
// (h-11 en móvil, h-8 desde md) sin heredar el tamaño de los botones de página.
const ACTION_BTN_CLS = cn(
  "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-xs font-medium",
  "border border-border bg-transparent text-fg hover:bg-bg-subtle",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors",
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
);

function ActionButton({ icon, label, onClick, disabled, tone = "default" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        ACTION_BTN_CLS,
        tone === "danger" && "text-status-error border-status-error-soft hover:bg-status-error-soft",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// Métricas de los últimos 7 días. Se pide recién al abrir el panel (lazy): la
// página es de administración y no necesita N llamadas para pintarse. Un fallo
// acá no rompe nada: la tarjeta sigue operable, solo no muestra números.
function AgentStatsPanel({ agentId }) {
  const { t } = useTranslation();
  const [state, setState] = useState("loading"); // loading|ready|error|missing
  const [stats, setStats] = useState({});

  useEffect(() => {
    let alive = true;
    setState("loading");
    getAiAgentStats(agentId, 7)
      .then((res) => {
        if (!alive) return;
        setStats(res?.stats || {});
        setState("ready");
      })
      .catch((err) => {
        if (!alive) return;
        setState(isEndpointMissing(err) ? "missing" : "error");
      });
    return () => {
      alive = false;
    };
  }, [agentId]);

  if (state === "loading") {
    return (
      <div className="flex items-center gap-1.5 py-2 text-xs text-fg-muted" role="status">
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
        {t("Cargando métricas…")}
      </div>
    );
  }

  if (state === "missing" || state === "error") {
    return (
      <p className="py-2 text-xs text-fg-muted" role="status">
        {state === "missing"
          ? t("Las métricas del agente están en construcción.")
          : t("No se pudieron cargar las métricas.")}
      </p>
    );
  }

  const tiles = [
    { key: "replies", label: t("Respuestas") },
    { key: "conversations", label: t("Conversaciones") },
    { key: "escalations", label: t("Derivaciones") },
    { key: "actions", label: t("Acciones") },
  ];

  return (
    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-2">
      {tiles.map((tile) => (
        <div key={tile.key} className="rounded-md border border-border px-2 py-1.5">
          <dt className="text-[12px] md:text-[11px] text-fg-muted">{tile.label}</dt>
          <dd className="text-sm font-semibold text-fg tabular-nums">
            {Number(stats?.[tile.key] || 0).toLocaleString()}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function AgentCard({
  agent,
  catalog,
  scope,
  busy,
  canConfigure,
  onEdit,
  onDuplicate,
  onToggleStatus,
  onRequestDelete,
}) {
  const { t } = useTranslation();
  const [statsOpen, setStatsOpen] = useState(false);

  const isActive = agent.status === "active";
  // El agente está por encima del alcance del rol: se puede mirar y pausar, pero
  // ni duplicarlo (crearía otro igual de alto) ni borrarlo.
  const locked = agent.locked_for_caller === true;
  const provider = providerSummary(agent, catalog);
  const offLabels = agentDisabledScopeLabels(agent, scope);
  const showScope = scope.length > 0 || offLabels.length > 0;

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-bg-subtle text-lg shrink-0"
          aria-hidden="true"
        >
          {agent.avatar || "🤖"}
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-fg break-words">{agent.name}</h3>
            <Badge tone={AGENT_STATUS_TONES[agent.status] || "neutral"} dot>
              {t(AGENT_STATUS_LABELS[agent.status] || agent.status)}
            </Badge>
            {locked && (
              <Badge tone="muted">{t("Fuera del alcance de tu rol")}</Badge>
            )}
          </div>

          {agent.description && (
            <p className="text-[12px] md:text-[11px] text-fg-muted break-words">{agent.description}</p>
          )}

          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(audienceLabel(catalog, agent.audience))}
            <span aria-hidden="true"> · </span>
            {provider.inherit
              ? t("Hereda del courier")
              : `${provider.label}${provider.model ? ` · ${provider.model}` : ""}`}
          </p>

          {showScope && (
            <p className="text-[12px] md:text-[11px] text-fg-muted">
              {offLabels.length === 0
                ? t("Responde en todos los canales")
                : t("No responde en: {{lista}}.", { lista: offLabels.join(", ") })}
            </p>
          )}

          <div>
            <button
              type="button"
              onClick={() => setStatsOpen((prev) => !prev)}
              aria-expanded={statsOpen}
              className={cn(
                "text-[12px] md:text-[11px] font-medium text-brand-600 hover:underline",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded",
              )}
            >
              {statsOpen ? t("Ocultar actividad") : t("Ver actividad (7 días)")}
            </button>
            {statsOpen && <AgentStatsPanel agentId={agent.id} />}
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-2">
            <ActionButton
              icon={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
              label={t("Editar")}
              onClick={() => onEdit(agent.id)}
              disabled={busy}
            />
            {canConfigure && !locked && (
              <ActionButton
                icon={<Copy className="w-3.5 h-3.5" aria-hidden="true" />}
                label={t("Duplicar")}
                onClick={() => onDuplicate(agent.id)}
                disabled={busy}
              />
            )}
            {/* Un agente fuera del alcance del rol solo se puede PAUSAR: ofrecer
                "Activar" (o "Activar" sobre un borrador) sería un control
                operable cuyo único desenlace es un 403. */}
            {(!locked || isActive) && (
              <ActionButton
                icon={
                  isActive ? (
                    <Pause className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    <Play className="w-3.5 h-3.5" aria-hidden="true" />
                  )
                }
                label={isActive ? t("Pausar") : t("Activar")}
                onClick={() => onToggleStatus(agent)}
                disabled={busy}
              />
            )}
            {canConfigure && !locked && (
              <ActionButton
                icon={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                label={t("Eliminar")}
                onClick={() => onRequestDelete(agent)}
                disabled={busy}
                tone="danger"
              />
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

// Tarjeta fija de la configuración base: no es un agente del contrato, es el
// asistente que ya existía. Abre el formulario original en el mismo panel.
function PrincipalCard({ onOpen }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-50 text-brand-600 shrink-0"
        aria-hidden="true"
      >
        <Sparkles className="w-5 h-5" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <h3 className="text-sm font-semibold text-fg">{t("Agente principal (configuración base)")}</h3>
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t(
            "Responde cuando ningún agente específico aplica. Instrucciones, proveedor, alcance y credencial del asistente del courier.",
          )}
        </p>
        <div className="pt-1">
          <ActionButton
            icon={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
            label={t("Configurar")}
            onClick={onOpen}
          />
        </div>
      </div>
    </li>
  );
}

function ConflictNotice({ conflicts, catalog }) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg border border-status-warning-soft bg-status-warning-soft px-3 py-2.5 space-y-1"
      role="status"
    >
      <p className="flex items-center gap-1.5 text-xs font-medium text-status-warning">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        {t("Posible conflicto de alcance")}
      </p>
      <p className="text-[12px] md:text-[11px] text-fg-muted">
        {t(
          "Estos agentes están activos para la misma audiencia y podrían responder a la vez en el mismo canal. Ajusta su alcance o pausa uno.",
        )}
      </p>
      <ul className="space-y-0.5">
        {conflicts.map((conflict) => (
          <li
            key={`${conflict.aId}-${conflict.bId}`}
            className="text-[12px] md:text-[11px] text-fg"
          >
            {t("{{a}} y {{b}}", { a: conflict.aName, b: conflict.bName })}
            <span className="text-fg-muted">
              <span aria-hidden="true"> · </span>
              {t(audienceLabel(catalog, conflict.audience))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AgentsSkeleton() {
  return (
    <div className="divide-y divide-border" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="py-4 first:pt-0 space-y-2">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40 rounded" />
              <Skeleton className="h-3 w-56 rounded" />
            </div>
          </div>
          <Skeleton className="h-11 md:h-8 w-full max-w-xs rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * Lista de agentes.
 * @param {object} props
 * @param {Array} props.agents
 * @param {object} props.catalog - { providers, capabilities, audiences, channels, caller }
 * @param {Array} props.scope - canales del tenant (getAiChannelScope)
 * @param {boolean} props.loading
 * @param {string|null} props.busyId - agente con una acción en vuelo
 * @param {Function} props.onNew
 * @param {Function} props.onEdit
 * @param {Function} props.onDuplicate
 * @param {Function} props.onToggleStatus
 * @param {Function} props.onDelete
 * @param {Function} props.onOpenLegacy
 */
export default function AgentsList({
  agents,
  catalog,
  scope,
  loading,
  busyId,
  onNew,
  onEdit,
  onDuplicate,
  onToggleStatus,
  onDelete,
  onOpenLegacy,
}) {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState(null);
  // El rol del llamador viene con el catálogo (el padre ya lo carga); sin ese
  // dato normalizeCaller() deja todo permitido.
  const permissions = normalizeCaller(catalog?.caller);

  const handleCancelDelete = useCallback(() => setPendingDelete(null), []);
  const handleConfirmDelete = useCallback(() => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (target) onDelete(target);
  }, [pendingDelete, onDelete]);

  const conflicts = detectScopeConflicts(agents);
  const isEmpty = !loading && agents.length === 0;

  return (
    <section className="space-y-4" aria-label={t("Agentes de IA")}>
      {loading ? (
        <AgentsSkeleton />
      ) : (
        <>
          {conflicts.length > 0 && <ConflictNotice conflicts={conflicts} catalog={catalog} />}

          {isEmpty && (
            <EmptyState
              icon={<Bot className="w-6 h-6" aria-hidden="true" />}
              title={t("Todavía no hay agentes")}
              description={t(
                "El agente principal sigue atendiendo los mensajes con la configuración base. Crea agentes para responder distinto según la audiencia.",
              )}
              // Sin permiso de configuración no se ofrece crear: un CTA que
              // termina en 403 es peor que no tenerlo.
              action={
                permissions.can_configure ? (
                  <Button onClick={onNew} iconLeft={<Bot className="w-4 h-4" aria-hidden="true" />}>
                    {t("Crear mi primer agente")}
                  </Button>
                ) : undefined
              }
            />
          )}

          <ul className="divide-y divide-border">
            <PrincipalCard onOpen={onOpenLegacy} />
            {agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                catalog={catalog}
                scope={scope}
                busy={busyId === agent.id}
                canConfigure={permissions.can_configure}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onToggleStatus={onToggleStatus}
                onRequestDelete={setPendingDelete}
              />
            ))}
          </ul>
        </>
      )}

      <Modal
        open={!!pendingDelete}
        onClose={handleCancelDelete}
        title={t("Eliminar agente")}
        footer={
          <>
            <Button variant="ghost" onClick={handleCancelDelete}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t("Vas a eliminar este agente:")}{" "}
          <span className="font-medium">{pendingDelete?.name}</span>
        </p>
        <p className="mt-2 text-sm text-fg-muted">
          {t(
            "Dejará de responder de inmediato y su configuración se borrará. Esta acción no se puede deshacer.",
          )}
        </p>
      </Modal>
    </section>
  );
}
